import type { Constraint, Phase, FormationInput } from "../../domain/src/types.js";
/**
 * Everything the trainer says out loud, assembled from the string tables.
 *
 * The wording lives in the domain rather than the UI so it is testable, and so
 * the distinction that matters most — rulebook versus convention — is stated in
 * one place instead of being scattered through markup. Every function takes a
 * locale and defaults to English.
 */

import { t, withoutArticle } from "./i18n.js";
import { backRowMiddle } from "../../domain/src/libero.js";
import { isFrontRow, slotsFor } from "../../domain/src/rotation.js";

/** Which on-court roles each pickable position covers. */
const POSITION_ROLES: Record<string, string[]> = {
  S: ["S"],
  OH: ["OH1", "OH2"],
  OPP: ["OPP"],
  MB: ["MB1", "MB2"],
  L: ["L"],
  ALL: [],
};

/** The positions a beginner can pick from on the title screen. */
export function positions(locale = "en") {
  return Object.keys(POSITION_ROLES).map((id) => ({
    id,
    label: t(locale, `positions.${id}.label`),
    nickname: t(locale, `positions.${id}.nickname`),
    blurb: t(locale, `positions.${id}.blurb`),
    roles: POSITION_ROLES[id],
  }));
}

/** How a coach would refer to a role out loud. */
export const roleLabel = (role: string, locale = "en") => t(locale, `roles.${role}`);

/** The short name shown on the badge above a player's head on court. */
export const sceneLabel = (role: string, locale = "en") =>
  t(locale, `sceneLabels.${role}`);

/** How a coach would name a zone: "front left", "קדמי שמאל". */
export const zoneName = (zone: number, locale = "en") => t(locale, `zones.${zone}`);

/**
 * One constraint, phrased as an instruction to the selected player.
 * `describeConstraint(constraint, roleLabel(otherRole, locale), locale)`
 */
export function describeConstraint(constraint: Constraint, otherLabel: string, locale = "en") {
  return t(locale, `constraints.${constraint.requirement}`, {
    other: otherLabel,
    toOther: withoutArticle(otherLabel),
    zone: constraint.other,
  });
}

/**
 * The same sentence, split around the role it names, so the interface can draw
 * that role as a coloured badge matching the one above their head on court.
 *
 * Splitting rather than string-matching matters in Hebrew, where a preposition
 * glues onto the noun and swallows its article: the sentence reads "משמאל ל" +
 * "מוסרת", and only the noun belongs inside the badge.
 */
export function constraintParts(constraint: Constraint, otherLabel: string, locale = "en") {
  const MARK = "\u0000";
  const sentence = t(locale, `constraints.${constraint.requirement}`, {
    other: MARK + otherLabel + MARK,
    toOther: MARK + withoutArticle(otherLabel) + MARK,
    zone: constraint.other,
  });
  const [before, label, after] = sentence.split(MARK);
  return { before, label, after };
}

/**
 * The headline distinction of the whole trainer: the rulebook binds the
 * receiving team, and only the receiving team (Rule 7.4) — and only until the
 * ball is struck (Rule 7.4.4).
 */
export function legalityBadge({ weServe, phase, locale = "en" }: { weServe: boolean; phase: Phase; locale?: string }) {
  if (phase === "switch" || phase === "defence") {
    return {
      tone: "free",
      title: t(locale, "badge.freeTitle"),
      detail: t(locale, "badge.freeDetail"),
    };
  }

  if (weServe) {
    return {
      tone: "tactical",
      title: t(locale, "badge.tacticalTitle"),
      detail: t(locale, "badge.tacticalDetail"),
    };
  }

  return {
    tone: "ruleEnforced",
    title: t(locale, "badge.ruleEnforcedTitle"),
    detail: t(locale, "badge.ruleEnforcedDetail"),
  };
}

/** A short coaching note for the current situation. */
export function coachNote({
  rotation,
  weServe,
  phase,
  role,
  locale = "en",
  libero = true,
}: FormationInput & { role?: string | null; locale?: string }) {
  const slots = slotsFor(rotation);
  const setterZone = slots.get("S")!;
  const middle = backRowMiddle(rotation);
  const middleServes = libero && weServe && slots.get(middle) === 1;

  if (middleServes && (role === "L" || role === middle)) {
    return t(locale, "coach.liberoMayNotServe", {
      middle: roleLabel(middle, locale),
    });
  }

  if (phase === "defence") return t(locale, "coach.defence");

  if (phase === "switch") return t(locale, "coach.shift");

  if (phase === "base") {
    return t(locale, "coach.base", {
      setter: setterZone,
      hitters: t(locale, isFrontRow(setterZone) ? "coach.two" : "coach.three"),
    });
  }

  if (weServe) return t(locale, "coach.serving");

  // Rotation 4 stacks left so the front outside can pass and still attack
  // from zone 4; the note names the two neighbours she must not cross.
  if (rotation === 4) return t(locale, "coach.receiveStack");

  // With the opposite in the back row she is the deep cover, and that is the
  // most useful thing to say about the shape on screen.
  const oppositeCovers = !isFrontRow(slots.get("OPP")!);
  return t(locale, oppositeCovers ? "coach.receiveDeepCover" : "coach.receive");
}
